import { useCallback, useMemo, useState } from 'react'
import { ArrowUpRight, X } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ALL,
  BREADCRUMB,
  CONTEXT_PILL,
  COPY,
  MODULE_LABEL,
  ODOO_OPTIONS,
  PAGE_LEDE,
  PAGE_TITLE,
  PANELS,
  ROUTES,
  TEMPLATE_PACKS,
  VERTICAL_CHIPS,
  cellLabel,
  daysSavedLabel,
  emptyBody,
  isHeroPack,
  matchesFilters,
  odooOptionLabel,
  ownerMeta,
  packOwner,
  packStatusPill,
  parseOdooFilter,
  provenInLabel,
  reuseLabel,
  versionLabel,
  type OdooFilter,
  type VerticalFilter,
} from '@/demo/content/templateLibrary'
import { cn } from '@/lib/utils'

/**
 * 2.3 Projects & library — the template library (industry × expertise).
 *
 * The library is where the group compounds: every project either spends from
 * it or pays into it. So the table is ordered by reuse — the score the group
 * invests on — and the three panels state what came in this quarter, what was
 * retired, and what the whole thing is worth on a rollout.
 *
 * Only the hero pack opens a detail view this round; the rest render as text
 * rather than links that would go nowhere, and the table says so once.
 * Every string, figure, id and route resolves from the content module.
 */
export default function TemplateLibraryPage() {
  const [vertical, setVertical] = useState<VerticalFilter>(ALL)
  const [odoo, setOdoo] = useState<OdooFilter>(ALL)

  const rows = useMemo(
    () => TEMPLATE_PACKS.filter((pack) => matchesFilters(pack, vertical, odoo)),
    [vertical, odoo]
  )

  const filtered = vertical !== ALL || odoo !== ALL

  const onVerticalClicked = useCallback((key: VerticalFilter) => setVertical(key), [])
  const onOdooChanged = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setOdoo(parseOdooFilter(event.target.value)),
    []
  )
  const onClearClicked = useCallback(() => {
    setVertical(ALL)
    setOdoo(ALL)
  }, [])

  return (
    <AppShell
      moduleLabel={MODULE_LABEL}
      breadcrumb={BREADCRUMB}
      contextPill={CONTEXT_PILL}
      nav={libraryNav}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <header className="max-w-3xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{PAGE_TITLE}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">{PAGE_LEDE}</p>
      </header>

      {/* ── Filters: the two axes the library is published against ──────── */}
      <section className="mt-5 flex flex-wrap items-end gap-x-8 gap-y-4" aria-label={COPY.filters.verticalLegend}>
        <div>
          <SectionLabel>{COPY.filters.verticalLegend}</SectionLabel>
          <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={COPY.filters.verticalLegend}>
            {VERTICAL_CHIPS.map((chip) => {
              const active = chip.key === vertical
              return (
                <button
                  key={chip.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onVerticalClicked(chip.key)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] transition-colors',
                    active
                      ? 'border-mint bg-accent font-semibold text-accent-foreground'
                      : 'border-border bg-card text-ink-soft hover:bg-muted'
                  )}
                >
                  {chip.label}
                  {chip.count !== undefined && (
                    <span className="v-figure tabular-nums text-muted-foreground">{chip.count}</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <label htmlFor="odoo-filter" className="block">
            <SectionLabel>{COPY.filters.versionLabel}</SectionLabel>
          </label>
          <select
            id="odoo-filter"
            value={String(odoo)}
            onChange={onOdooChanged}
            className="mt-1.5 rounded-md border border-input bg-card px-2.5 py-1.5 text-[13px] text-foreground"
          >
            {ODOO_OPTIONS.map((option) => (
              <option key={String(option)} value={String(option)}>
                {odooOptionLabel(option)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 pb-1">
          <span className="v-figure text-[13px] tabular-nums text-muted-foreground">
            {COPY.filters.resultCount(rows.length, TEMPLATE_PACKS.length)}
          </span>
          {filtered && (
            <button
              type="button"
              onClick={onClearClicked}
              className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-[13px] text-ink-soft hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              {COPY.empty.action}
            </button>
          )}
        </div>
      </section>

      {/* ── Packs ───────────────────────────────────────────────────────── */}
      {rows.length > 0 ? (
        <section aria-labelledby="packs-caption" className="mt-5 rounded-lg border border-border bg-card">
          <Table>
            <caption id="packs-caption" className="px-4 py-2.5 text-left text-xs text-muted-foreground">
              {COPY.tableCaption}
            </caption>
            <TableHeader>
              <TableRow>
                <TableHead>{COPY.columns.pack}</TableHead>
                <TableHead>{COPY.columns.cell}</TableHead>
                <TableHead>{COPY.columns.contains}</TableHead>
                <TableHead className="text-right">{COPY.columns.reuse}</TableHead>
                <TableHead className="text-right">{COPY.columns.timeSaved}</TableHead>
                <TableHead>{COPY.columns.owner}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((pack) => {
                const owner = packOwner(pack)
                const pill = packStatusPill(pack)
                const hero = isHeroPack(pack)
                return (
                  <TableRow key={pack.id}>
                    <TableCell className="max-w-xs px-4 align-top whitespace-normal">
                      <span className="flex flex-wrap items-center gap-1.5">
                        {hero ? (
                          <Link
                            to={ROUTES.templateDetail(pack.id)}
                            className="inline-flex items-center gap-1 text-[13px] font-medium text-foreground hover:underline"
                          >
                            {pack.name}
                            <ArrowUpRight className="h-3 w-3 text-accent-foreground" aria-hidden />
                          </Link>
                        ) : (
                          <span className="text-[13px] font-medium text-foreground">{pack.name}</span>
                        )}
                        {pill && <StatusPill tone={pill.tone}>{pill.label}</StatusPill>}
                      </span>
                      <span className="v-figure mt-0.5 block text-xs tabular-nums text-muted-foreground">
                        {versionLabel(pack)}
                      </span>
                    </TableCell>

                    <TableCell className="max-w-[15rem] align-top whitespace-normal">
                      <span className="text-[13px] text-ink-soft">{cellLabel(pack)}</span>
                      {pack.crossVertical && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{provenInLabel(pack)}</span>
                      )}
                    </TableCell>

                    <TableCell className="max-w-sm align-top whitespace-normal">
                      <ul className="flex flex-wrap gap-x-3 gap-y-0.5">
                        {pack.contains.map((item) => (
                          <li key={item} className="text-[13px] text-ink-soft">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </TableCell>

                    <TableCell className="v-figure text-right align-top text-[13px] tabular-nums text-foreground">
                      {reuseLabel(pack)}
                    </TableCell>

                    <TableCell className="v-figure text-right align-top text-[13px] tabular-nums text-ink-soft">
                      {daysSavedLabel(pack)}
                    </TableCell>

                    <TableCell className="px-4 align-top whitespace-normal">
                      <Link
                        to={ROUTES.person(owner.id)}
                        className="text-[13px] font-medium text-foreground hover:underline"
                      >
                        {owner.name}
                      </Link>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{ownerMeta(owner)}</span>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">{COPY.nonHeroNote}</p>
        </section>
      ) : (
        <section
          aria-labelledby="packs-empty-heading"
          className="mt-5 rounded-lg border border-border bg-card px-4 py-5"
        >
          <h2 id="packs-empty-heading" className="font-serif text-lg font-semibold text-foreground">
            {COPY.empty.title}
          </h2>
          <p className="mt-1.5 max-w-2xl text-[13px] text-ink-soft">{emptyBody(vertical, odoo)}</p>
          <button
            type="button"
            onClick={onClearClicked}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-[13px] font-medium text-accent-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            {COPY.empty.action}
          </button>
        </section>
      )}

      {/* ── What the library took in, retired, and is worth ─────────────── */}
      <section className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label={PANELS.contribution.label}>
        <KpiCard
          label={PANELS.contribution.label}
          figure={
            <>
              {PANELS.contribution.figure}
              <span className="ml-1.5 font-sans text-sm font-normal text-muted-foreground">
                {PANELS.contribution.unit}
              </span>
            </>
          }
          qualifier={PANELS.contribution.qualifier}
        />
        <KpiCard
          label={PANELS.retired.label}
          figure={
            <>
              {PANELS.retired.figure}
              <span className="ml-1.5 font-sans text-sm font-normal text-muted-foreground">
                {PANELS.retired.unit}
              </span>
            </>
          }
          qualifier={PANELS.retired.qualifier}
        />
        <KpiCard
          className="bg-accent"
          label={PANELS.effect.label}
          figure={PANELS.effect.figure}
          qualifier={
            <>
              {PANELS.effect.qualifier.before}
              <Link
                to={PANELS.effect.qualifier.link.to}
                className="font-medium text-accent-foreground hover:underline"
              >
                {PANELS.effect.qualifier.link.label}
              </Link>
              {PANELS.effect.qualifier.after}
            </>
          }
        />
      </section>

      <DensityNote>
        {COPY.densityNote.before}
        <Link
          to={COPY.densityNote.link.to}
          className="font-medium text-accent-foreground not-italic hover:underline"
        >
          {COPY.densityNote.link.label}
        </Link>
        {COPY.densityNote.after}
      </DensityNote>
    </AppShell>
  )
}
